import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../css/uploadSOBTarget.css";
import { BASE_URL } from '../config';
import { getAuthHeaders } from "../services/useAuth";

const SobUploadPage = () => {
  const [fromDate, setFromDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // ✅ UPDATED

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/sob-targets/employee`, {
          headers: getAuthHeaders()
        });
        setEmployees(data);
      } catch (err) {
        setError('Failed to fetch employee data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';
    if (parts[0].length === 4) {
      return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
    }
    return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
  };

  const toYYYYMMDD = (ddmmyyyy) => {
    const [dd, mm, yyyy] = ddmmyyyy.split('-');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleFileUpload = async () => {
    setError('');
    setUploadStatus('');

    if (!selectedFile || !fromDate) {
      setError('Please select both a date and a file');
      return;
    }

    const normalizedInputDate = normalizeDate(fromDate);

    if (selectedFile.name.endsWith('.csv')) {
      try {
        const fileContent = await selectedFile.text();
        const rows = fileContent.split('\n').filter(row => row.trim() !== '');

        if (rows.length < 2) {
          setError('CSV file must contain at least one data row.');
          return;
        }

        const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
        const dateColumnIndex = headers.findIndex(header => header.includes('from_date') || header.includes('date'));

        if (dateColumnIndex === -1) {
          setError('CSV file must contain a "from_date" column.');
          return;
        }

        const keyMap = {
          'emp id': 'employee_id',
          'empid': 'employee_id',
          'employee name': 'employee_name',
          'name': 'employee_name',
          'division': 'division',
          'state': 'state',
          'hq': 'hq',
          'designation': 'designation',
          'phone': 'phone',
          'target': 'target',
          'from_date': 'from_date',
          'from date': 'from_date',
          'date': 'from_date'
        };

        const csvData = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i].split(',');
          const rawDate = row[dateColumnIndex]?.trim();
          const rowDate = normalizeDate(rawDate);

          if (!rowDate || rowDate !== normalizedInputDate) {
            setError(`Row ${i + 1} has from_date "${rawDate}" which doesn't match selected From Date: ${normalizedInputDate}`);
            return;
          }

          const rowData = {};
          headers.forEach((header, idx) => {
            const key = keyMap[header.trim().toLowerCase()] || header.trim().toLowerCase();
            rowData[key] = row[idx]?.trim();
          });

          csvData.push({
            id: Date.now() + i,
            employee_id: rowData.employee_id || '',
            employee_name: rowData.employee_name || '',
            division: rowData.division || '',
            state: rowData.state || '',
            hq: rowData.hq || '',
            designation: rowData.designation || '',
            phone: rowData.phone || '',
            target: rowData.target || '',
            start_date: rowData.from_date || normalizedInputDate
          });
        }

        setEmployees(csvData);
        setCurrentPage(1);
      } catch (err) {
        setError('Error reading CSV file. Please check the format.');
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('from_date', toYYYYMMDD(normalizedInputDate));

    try {
      setUploadStatus('Uploading...');
      const response = await axios.post(
        `${BASE_URL}/api/sob-targets/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setUploadStatus(response.data.message || 'Upload successful!');
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
    } catch (err) {
      let errorMsg = 'Upload failed. Please try again.';
      const resData = err.response?.data;

      if (Array.isArray(resData)) {
        errorMsg = resData.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof resData === 'object') {
        errorMsg = resData.detail || resData.message || errorMsg;
      }

      setError(errorMsg);
      setUploadStatus('');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
      setUploadStatus('');
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = employees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(employees.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="sob-upload-container">
      <Header />
      <Navbar />

      <div className="upload-section">
        <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4"><span class="text-blue-700"> Select </span><span class="text-blue-500">file to upload</span></h1>

        <div className="upload-fields">
          <div className="upload-field">
            <label>From Date:- </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
            />
          </div>
          <div className="upload-field">
            <label>File (CSV/Excel):- </label>
            <input
              id="file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              required
            />
          </div>
          <div className="upload-button">
            <button onClick={handleFileUpload}>Go</button>
          </div>
          {uploadStatus && <div className="upload-status">{uploadStatus}</div>}
        </div>
      </div>

      <div className="info-section">
       
        <h1 className="text-2xl sm:text-3xl  text-center  mb-4">SOB Target Upload Information</h1>

        <div className="info-content">
          {selectedFile ? (
            <div>
              <p><strong>File Name:</strong> {selectedFile.name}</p>
              <p><strong>File Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB</p>
              <p><strong>File Type:</strong> {selectedFile.type || selectedFile.name.split('.').pop().toUpperCase()}</p>
              <p><strong>Selected Date:</strong> {normalizeDate(fromDate) || 'Not selected'}</p>
              <p><strong>Total Records:</strong> {employees.length}</p>
            </div>
          ) : (
            <p>No file selected. Please choose a CSV or Excel file to upload.</p>
          )}
        </div>
      </div>

      {uploadStatus && <div className="status-message success">{uploadStatus}</div>}
      {error && <div className="status-message error">{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      <div className="employee-section">
        <div className="">
          <h2 className="text-2xl sm:text-3xl  text-center  mb-4">Employee List</h2>
        </div>
        <div className="employee-table-container">
          {loading ? (
            <p>Loading employee data...</p>
          ) : error ? (
            <p className="error">{typeof error === 'string' ? error : JSON.stringify(error)}</p>
          ) : (
            <>
              <table className="employee-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>Empid</th>
                    <th>Employee</th>
                    <th>Division</th>
                    <th>State</th>
                    <th>HQ</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Target</th>
                    <th>From Date</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((employee, index) => (
                    <tr key={employee.id || index}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{employee.employee_id}</td>
                      <td>{employee.employee_name}</td>
                      <td>{employee.division}</td>
                      <td>{employee.state}</td>
                      <td>{employee.hq}</td>
                      <td>{employee.designation}</td>
                      <td>{employee.phone}</td>
                      <td>{employee.target}</td>
                      <td>{employee.start_date ? normalizeDate(employee.start_date) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ✅ Tailwind Pagination UI */}
              {employees.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                  <div className="flex items-center mb-2 sm:mb-0">
                    <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">View</label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setItemsPerPage(parseInt(e.target.value));
                      }}
                      className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                    >
                      {[10, 20, 30, 50].map((count) => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600 font-medium">
                      {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, employees.length)} of {employees.length}
                    </span>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &larr;
                    </button>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &rarr;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SobUploadPage;
